import React, { useMemo } from "react";
import CRUDFormDialog from "./CRUDFormDialog";
import { ResourceName } from "../descriptors";
import { useRevalidator } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { defineMessages, FormattedMessage, MessageDescriptor } from "react-intl";
import { FormActionButton } from "./CRUDForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";

const translations = defineMessages({
    create: {
        id: "create",
        defaultMessage: "Create",
    },
    update: {
        id: "update",
        defaultMessage: "Update",
    },
});

/// button to open a create or update resource form in a dialog
const ResourceFormButton: React.FC<{
    resourceName: ResourceName;
    action: "create" | "update";
    actionLabel?: string; // stable data-action value, defaults to resourceName-action
    dialogTitle: MessageDescriptor; // title of the form dialog
    id?: number; // id to update when action is update
    extraButtons?: FormActionButton[];
    extraRequestData?: Record<string, any>; /// extra data to be sent with the request
    size?: "small" | "medium" | "large";
    buttonType?: "text" | "icon" | "section-icon";
    formFields?: string[]; // fields to be shown in the form, if not provided all fields will be shown
}> = ({
    resourceName,
    action,
    actionLabel = `${resourceName}-${action}`,
    dialogTitle,
    extraButtons,
    extraRequestData,
    id,
    buttonType = "text",
    size = "small",
    formFields,
}) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const revalidator = useRevalidator();
    const actionMessage = useMemo(() => translations[action], [action]);

    return (
        <>
            {buttonType !== "text" ? (
                <IconButton
                    data-action={actionLabel}
                    onClick={() => {
                        setDialogOpen(true);
                    }}
                    sx={
                        buttonType === "section-icon"
                            ? {
                                  bgcolor: "primary.main",
                                  color: "primary.contrastText",
                                  "&:hover": {
                                      bgcolor: "primary.dark",
                                  },
                              }
                            : {
                                  color: "primary.main",
                              }
                    }
                >
                    <FontAwesomeIcon icon={action == "create" ? faPlus : faPencil} />
                </IconButton>
            ) : (
                <Button
                    data-action={actionLabel}
                    variant="contained"
                    size={size}
                    onClick={() => {
                        setDialogOpen(true);
                    }}
                >
                    <FormattedMessage {...actionMessage} />
                </Button>
            )}
            <CRUDFormDialog
                open={dialogOpen}
                title={dialogTitle}
                hide={() => setDialogOpen(false)}
                action={action}
                id={id}
                resourceName={resourceName}
                onSuccess={() => {
                    setDialogOpen(false);
                    revalidator.revalidate();
                }}
                extraButtons={extraButtons}
                extraRequestData={extraRequestData}
                formFields={formFields}
            />
        </>
    );
};

export default ResourceFormButton;
